import React, { useRef } from 'react'
import styles from './FeatureMain.module.css'
import FeatureContainer from '../FeatureContainer/FeatureContainer'
import Reveal from '../Reveal/Reveal'
import SectionHeading from '../SectionHeading/SectionHeading'
import FeatureBlock from '../FeatureBlock/FeatureBlock'
import JobCategorySection from '../JobList/JobCategorySection'
import { FavoriteOffersProvider } from '../JobList/FavoriteOffersContext'
import Footer from '../Footer/Footer'
import FloatingCta from '../FloatingCta/FloatingCta'
import { useFloatingCtaTarget } from '../FloatingCta/useFloatingCtaTarget'
import { FeatureHeadlineSegment, FeatureMainComponentProps } from './types'

/** 「働く魅力」ブロック番号の桁数(01, 02, ...) */
const NUMBER_PAD_LENGTH = 2
/** 画像の左右を交互配置するための剰余の除数 */
const IMAGE_ALIGN_ALTERNATION = 2

const formatNumber = (n: number) => String(n).padStart(NUMBER_PAD_LENGTH, '0')

/**
 * 見出し（title / eyebrow）・本文のセグメント配列をインラインで描画する。
 * accent で色付け・breakBefore で直前に改行する。
 */
const renderHeadlineSegments = (
    segments: FeatureHeadlineSegment[],
    accentClassName: string
) =>
    segments.map((segment, index) => {
        const className =
            [
                segment.accent ? accentClassName : '',
                segment.hangEnd ? styles.headlineTitleHangEnd : '',
            ]
                .filter(Boolean)
                .join(' ') || undefined
        const style = segment.fontSize
            ? { fontSize: segment.fontSize }
            : undefined
        return (
            <React.Fragment key={index}>
                {segment.breakBefore && <br />}
                <span className={className} style={style}>
                    {segment.text}
                </span>
            </React.Fragment>
        )
    })

/**
 * 特設LPの標準レイアウト一式。
 * hero → headline → サムネ → 働く魅力 → 求人特集 → Footer ＋ 固定CTA を
 * データ（FeatureMainProps）だけで描画する。
 * 標準レイアウトから外れるLPは、components/Features 配下の部品を直接組み合わせる。
 */
export default function FeatureMain({
    hero,
    headline,
    thumbnail,
    featuresTitle,
    featuresTitleAnalyticsLabel,
    features,
    jobs,
    floatingCta,
    pc,
    accentColor,
    projectBranch,
}: FeatureMainComponentProps) {
    // 求人カードのアイコン・ボタン・バッジは、LPのaccentColorではなく
    // サイト(hotelslabo / foodslabo)のテーマカラーを使う。

    // 求人セクションへスクロール＆視界に入ったら固定CTAを隠す
    const { targetRef, isCtaHidden, scrollToTarget } =
        useFloatingCtaTarget<HTMLDivElement>()
    const featuresRef = useRef<HTMLDivElement>(null)

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const scrollToFeatures = () => {
        featuresRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }

    return (
        <>
            {pc && (
                <>
                    <div
                        className={styles.pcBackground}
                        style={
                            {
                                backgroundImage: `url(${pc.background})`,
                                ...(pc.overlay && {
                                    '--feature-pc-overlay': pc.overlay,
                                }),
                            } as React.CSSProperties
                        }
                        aria-hidden="true"
                    />

                    <div
                        className={[
                            styles.pcTitle,
                            pc.titleFont === 'sans-serif'
                                ? styles.pcTitleSansSerif
                                : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {pc.eyebrow && (
                            <p className={styles.pcTitleEyebrow}>
                                <span className={styles.pcTitleEyebrowLine} />
                                {pc.eyebrow}
                                <span className={styles.pcTitleEyebrowLine} />
                            </p>
                        )}
                        <p className={styles.pcTitleText}>
                            {pc.titleLines.map((line, index) => (
                                <span key={index}>{line}</span>
                            ))}
                        </p>
                    </div>

                    <nav
                        className={styles.pcContents}
                        aria-label="ページ内リンク"
                        style={
                            {
                                '--feature-contents-color': pc.contentsColor,
                            } as React.CSSProperties
                        }
                    >
                        <div className={styles.pcContentsInner}>
                            <p className={styles.pcContentsLabel}>contents</p>
                            <ul className={styles.pcContentsList}>
                                <li>
                                    <button
                                        type="button"
                                        className={styles.pcContentsLink}
                                        onClick={scrollToTop}
                                    >
                                        TOP
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={styles.pcContentsLink}
                                        onClick={scrollToFeatures}
                                    >
                                        {featuresTitle.join('')}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        className={styles.pcContentsLink}
                                        onClick={scrollToTarget}
                                    >
                                        {jobs.title.join('')}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </nav>
                </>
            )}

            <FeatureContainer
                accentColor={accentColor}
                projectBranch={projectBranch}
            >
                <div
                    className={styles.hero}
                    data-view-label={hero.analyticsLabel}
                >
                    <img
                        src={hero.image}
                        alt={hero.alt}
                        className={styles.heroPhoto}
                    />
                    {hero.panel && (
                        <img
                            src={hero.panel}
                            alt=""
                            aria-hidden="true"
                            className={styles.heroPanel}
                        />
                    )}
                </div>

                <Reveal className={styles.headline}>
                    {headline.eyebrow && (
                        <p className={styles.headlineEyebrow}>
                            {renderHeadlineSegments(
                                headline.eyebrow,
                                styles.headlineTitleAccent
                            )}
                        </p>
                    )}
                    <h2 className={styles.headlineTitle}>
                        {renderHeadlineSegments(
                            headline.title,
                            styles.headlineTitleAccent
                        )}
                    </h2>
                    <div className={styles.headlineBody}>
                        {headline.paragraphs && (
                            <p>
                                {renderHeadlineSegments(
                                    headline.paragraphs,
                                    styles.headlineTitleAccent
                                )}
                            </p>
                        )}
                        {headline.accent && (
                            <p className={styles.headlineAccent}>
                                {headline.accent}
                            </p>
                        )}
                    </div>
                </Reveal>

                {thumbnail && (
                    <Reveal className={styles.thumbnailRow}>
                        <img
                            src={thumbnail.image}
                            alt={thumbnail.alt}
                            className={styles.thumbnail}
                        />
                    </Reveal>
                )}

                <div ref={featuresRef} className={styles.featuresSection}>
                    <SectionHeading
                        title={featuresTitle}
                        className={styles.sectionTitle}
                        analyticsLabel={featuresTitleAnalyticsLabel}
                    />

                    {features.map((feature, index) => (
                        <FeatureBlock
                            key={index}
                            number={formatNumber(index + 1)}
                            total={formatNumber(features.length)}
                            heading={feature.heading}
                            body={feature.body}
                            image={feature.image}
                            imageAlign={
                                feature.imageAlign ??
                                (index % IMAGE_ALIGN_ALTERNATION === 0
                                    ? 'right'
                                    : 'left')
                            }
                            analyticsLabel={feature.analyticsLabel}
                        />
                    ))}
                </div>

                <div ref={targetRef} className={styles.jobsSection}>
                    <SectionHeading
                        eyebrow={jobs.eyebrow}
                        title={jobs.title}
                        className={styles.jobsIntro}
                        analyticsLabel={jobs.analyticsLabel}
                    />

                    <FavoriteOffersProvider
                        offerIds={jobs.categories.flatMap(category =>
                            category.jobs.map(job => job.offerId)
                        )}
                    >
                        {jobs.categories.map(category => (
                            <JobCategorySection
                                key={category.id}
                                category={category}
                            />
                        ))}
                    </FavoriteOffersProvider>
                </div>

                <Footer />

                <FloatingCta
                    label={floatingCta.label}
                    analyticsLabel={floatingCta.analyticsLabel}
                    onClick={scrollToTarget}
                    hidden={isCtaHidden}
                />
            </FeatureContainer>
        </>
    )
}
